import { useCallback, useEffect, useMemo } from "react";

import {
  computeMembershipStatus,
  FormType,
  isBankDebitPaymentMethod,
  PaymentMethod,
  ProductTicketStatus,
  RouteBuilders,
  TicketingFormCategory,
} from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { useSnackbar } from "notistack";

import { useUpdateCreditCard } from "../helpers/useUpdateCreditCard";

import { useEditDonorChargeDate } from "./helpers/useEditDonorChargeDate";
import { useScheduleDonorSubscriptionEnd } from "./helpers/useScheduleDonorSubscriptionEnd";
import { getTicketCancellationStatus } from "./helpers/getTicketCancellationStatus";
import { CancellationReason } from "./PaymentDetails/CancelSubscriptionDialog/helpers/constants";
import { DonorPaymentControl } from "./types";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { useUrlBuilder } from "@/common/hooks/useUrlBuilder";

export interface DonorPaymentDetailDrawerProps {
  onClose: () => void;
  isOpen: boolean;
  content:
    | {
        formType: FormType;
        id: string;
        commandId: string;
        formId: string;
        subscriptionId?: string | null;
        organizationId: string;
      }
    | undefined;
  refetchTable?: () => void;
  initialControl?: DonorPaymentControl;
}

export const useDonorPaymentDetailDrawer = ({
  content,
  isOpen,
  refetchTable,
}: Pick<DonorPaymentDetailDrawerProps, "content" | "isOpen" | "refetchTable">) => {
  const {
    data: payment,
    isLoading: paymentLoading,
    refetch: refetchPayment,
  } = trpc.retrieveDonorDashboardTransaction.useQuery(
    { id: content?.id || "" },
    {
      enabled: Boolean(content?.id && isOpen),
      refetchOnMount: true,
    }
  );

  const {
    data: command,
    isLoading: commandLoading,
    refetch: refetchCommand,
  } = trpc.retrieveDonorCommand.useQuery(
    { id: content?.commandId || "" },
    {
      enabled: Boolean(content?.commandId && isOpen),
      refetchOnMount: true,
    }
  );

  const {
    data: form,
    isLoading: formLoading,
    refetch: refetchForm,
  } = trpc.retrieveForm.useQuery(
    { id: content?.formId || "", formType: content?.formType as FormType },
    {
      enabled: Boolean(content?.formId && isOpen),
      refetchOnMount: true,
    }
  );

  const {
    data: subscriptionData,
    isLoading: subscriptionLoading,
    refetch: refetchSubscription,
  } = trpc.retrieveDonorDashboardSubscription.useQuery(
    { id: content?.subscriptionId || "" },
    {
      enabled: Boolean(content?.subscriptionId && isOpen),
      refetchOnMount: true,
    }
  );

  const subscription = subscriptionData?.object;
  const organization = payment?.organization;

  const {
    data: receipt,
    isLoading: receiptLoading,
    refetch: refetchReceipt,
  } = trpc.retrieveDonorReceipt.useQuery(
    { id: payment?.receiptId || "" },
    { enabled: Boolean(payment?.receiptId && isOpen) }
  );

  const { data: tipRefundTokenData } = trpc.getDonorTipRefundToken.useQuery(
    { transactionId: content?.id || "" },
    { enabled: Boolean(content?.id && isOpen && payment && (payment.tip ?? 0) > 0) }
  );

  const refetch = useCallback(() => {
    void (content?.commandId && refetchCommand());
    void (content?.id && refetchPayment());
    void (content?.formId && refetchForm());
    void (content?.subscriptionId && refetchSubscription());
    void (content?.commandId && refetchReceipt());
    refetchTable?.();
  }, [
    content?.commandId,
    content?.id,
    content?.formId,
    content?.subscriptionId,
    refetchCommand,
    refetchPayment,
    refetchForm,
    refetchSubscription,
    refetchReceipt,
    refetchTable,
  ]);

  const { updateCreditCard } = useUpdateCreditCard();

  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { logAmplitudeEvent } = useAmplitude();
  const { isoLocale: hookIsoLocale } = useLocaleContext();
  const buildUrl = useUrlBuilder();
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());
  const success = query?.success;

  const clickOnDownloadReceipt = useCallback(() => {
    logAmplitudeEvent(AmplitudeEvents.DonorTransactionDrawerClickDownloadReceipt);
  }, [logAmplitudeEvent]);

  const clickDownloadETicket = useCallback(() => {
    logAmplitudeEvent(AmplitudeEvents.DonorTransactionDrawerClickDownloadETicket);
  }, [logAmplitudeEvent]);

  const { mutateAsync: execEditDonorCommandSubscriptionAmount, isPending: editDonationAmountIsLoading } =
    trpc.editDonorCommandSubscriptionAmount.useMutation();

  const editDonationAmount = useCallback(
    async (commandId: string, newAmount: number) => {
      const { data, error } = await execEditDonorCommandSubscriptionAmount({
        id: commandId,
        amount: newAmount,
      });
      if (error || !data) {
        enqueueSnackbar(t("dashboard", "editDonationAmountConfirmError"), {
          vibe: "danger",
        });
        return false;
      }
      refetch();
      enqueueSnackbar(t("dashboard", "editDonationAmountConfirmSuccess"), {
        vibe: "positive",
      });
      return true;
    },
    [execEditDonorCommandSubscriptionAmount, enqueueSnackbar, t, refetch]
  );

  const { editChargeDate, editChargeDateIsLoading } = useEditDonorChargeDate({ refetch });
  const { scheduleSubscriptionEnd, scheduleSubscriptionEndIsLoading } = useScheduleDonorSubscriptionEnd({
    refetch,
    timezone: subscription?.userTimezone,
  });

  const { mutateAsync: execCancelDonorCommandSubscription, isPending: cancelSubscriptionIsLoading } =
    trpc.cancelDonorCommandSubscription.useMutation();

  const cancelSubscription = useCallback(
    async (commandId: string, cancellationReason?: CancellationReason, cancellationFeedback?: string) => {
      const { data, error } = await execCancelDonorCommandSubscription({
        id: commandId,
        cancellationReason,
        cancellationFeedback,
      });
      if (error || !data) {
        enqueueSnackbar(t("dashboard", "donorTransactionTable.impossibleToCancelSubscription"), {
          vibe: "danger",
        });
        return false;
      }
      refetch();
      enqueueSnackbar(t("dashboard", "donorDashboard.transactions.subscriptionCanceledSuccess"), {
        vibe: "positive",
      });
      return true;
    },
    [execCancelDonorCommandSubscription, enqueueSnackbar, t, refetch]
  );

  // Snackbar for credit card update success redirect
  useEffect(() => {
    if (success === "true") {
      enqueueSnackbar(t("dashboard", "donorTransactionTable.paymentMethodEditionSuccess"), {
        vibe: "positive",
      });
    }
  }, [enqueueSnackbar, t, success]);

  const isMembership = form?.formCategory === TicketingFormCategory.MembershipV2;

  const activeProductTickets = useMemo(
    () =>
      command?.productTickets?.filter((ticket) =>
        [ProductTicketStatus.Active, ProductTicketStatus.CheckedIn].includes(ticket.status)
      ),
    [command?.productTickets]
  );

  const isActiveSubscription = payment?.subscriptionStatus === "Active" || payment?.subscriptionStatus === "PastDue";

  const hasAutomaticRenewal = useMemo(() => {
    if (!activeProductTickets?.length) {
      return false;
    }
    return activeProductTickets.length === 1 && Boolean(activeProductTickets[0].automaticRenewal);
  }, [activeProductTickets]);

  const membershipStatus = useMemo(() => {
    const expirationDate = activeProductTickets?.[0]?.expirationDate;
    if (!isMembership || !expirationDate) {
      return null;
    }
    return computeMembershipStatus(expirationDate, hasAutomaticRenewal || isActiveSubscription);
  }, [isMembership, activeProductTickets, isActiveSubscription, hasAutomaticRenewal]);

  const ticketCancellationStatus = useMemo(
    () => getTicketCancellationStatus(command?.productTickets),
    [command?.productTickets]
  );

  const tipRefundToken = tipRefundTokenData && "token" in tipRefundTokenData ? tipRefundTokenData.token : undefined;

  const tipRefundLink = useMemo(() => {
    if (!tipRefundToken) {
      return "";
    }
    return buildUrl({
      route: RouteBuilders.buildTipRefundPageLink({ isoLocale: hookIsoLocale, token: tipRefundToken }),
    });
  }, [tipRefundToken, buildUrl, hookIsoLocale]);

  const changeContributionLink = useMemo(() => {
    if (!tipRefundToken) {
      return "";
    }
    return buildUrl({
      route: RouteBuilders.buildChangeContributionPageLink({ isoLocale: hookIsoLocale, token: tipRefundToken }),
    });
  }, [tipRefundToken, buildUrl, hookIsoLocale]);

  const isRecurringPayment =
    Boolean(payment?.subscriptionId) && Boolean(content?.subscriptionId) && isActiveSubscription;

  const adjustTipLink = isRecurringPayment
    ? changeContributionLink
    : getOneTimeTipRefundLink(tipRefundLink, payment?.paymentMethod);

  return {
    payment,
    command,
    form,
    subscription,
    receipt,
    organization,
    isLoading:
      paymentLoading ||
      commandLoading ||
      formLoading ||
      (Boolean(content?.subscriptionId) && subscriptionLoading) ||
      (Boolean(payment?.receiptId) && receiptLoading),
    refetch,
    updateCreditCard,
    clickOnDownloadReceipt,
    clickDownloadETicket,
    isActiveSubscription,
    editDonationAmount,
    editDonationAmountIsLoading,
    editChargeDate,
    editChargeDateIsLoading,
    scheduleSubscriptionEnd,
    scheduleSubscriptionEndIsLoading,
    cancelSubscription,
    cancelSubscriptionIsLoading,
    isMembership,
    membershipStatus,
    ticketCancellationStatus,
    hasAutomaticRenewal,
    activeProductTickets,
    adjustTipLink,
    isRecurringPayment,
  };
};

const getOneTimeTipRefundLink = (tipRefundLink: string, paymentMethod: PaymentMethod | null | undefined) =>
  isBankDebitPaymentMethod(paymentMethod) ? "" : tipRefundLink;
